import { Injectable } from '@angular/core';
import { ApiJobService } from '@app/core/graphql/api-job.service';
import { ApiJobsService } from '@app/core/http/api-jobs/api-jobs.service';
import { FileObj, Job } from '@app/shared/interfaces/job';
import { ApiResponse } from '@app/shared/models/ApiResponse';
import { DeleteFileResponse } from '@app/shared/models/Job';
import { JobStatus } from '@app/shared/models/JobStatus';
import { LoginAccount } from '@app/shared/models/loginAccount';
import { GroupJob, PrintJob } from '@app/shared/models/PrintJob';
import { CustomHttpHeader } from '@app/shared/models/userBalance';
import { BehaviorSubject } from 'rxjs';
import { LoadingService } from '../loading/loading.service';
import { LoginAccountService } from '../login-account/login-account.service';
import { SharedService } from '../shared/shared.service';
import { StorageService } from '../storage/storage.service';
import { ToastService } from '../toast/toast.service';

@Injectable({
  providedIn: 'root',
})
export class JobService {
  jobList: GroupJob[] = [];
  jobList$: BehaviorSubject<GroupJob[]> = new BehaviorSubject([]);

  constructor(
    private storage: StorageService,
    private sharedService: SharedService,
    private apiJobsService: ApiJobsService,
    private apiJobService: ApiJobService,
    private toastService: ToastService,
    private loadingSvc: LoadingService,
    private loginAccountService: LoginAccountService
  ) { }

  public async generateKey(): Promise<string> {
    let key = `todo${parseInt(`${Math.random() * 100}`, 10)}`;
    let ret = await this.storage.get(key);

    while (ret) {
      key = `todo${parseInt(`${Math.random() * 100}`, 10)}`;
      ret = await this.storage.getJson(key);
    }
    return key;
  }
  public async read(): Promise<Job[]> {
    let todos: Array<Job> = [];
    const keys = await this.storage.keys();
    todos = await this.getTodos(keys);

    return todos;
  }
  public async getTodos(keys): Promise<Job[]> {
    let data = [];

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (key.startsWith('todo')) {
        const job = await this.storage.getJson(key);
        let displayTime = this.sharedService.timeAgo(job.data.time);

        job.data.displayTime = displayTime;
        data.push(job);
      }
    }

    return data.slice().sort(function (a, b) {
      return new Date(b.data.time).getTime() - new Date(a.data.time).getTime();
    });
  }
  public async create(key: string, todo: Job) {
    return await this.storage.setJson(key, todo);
  }

  public async update(key, todo: Job) {
    return await this.storage.set(key, todo);
  }

  public async delete(key: string) {
    return await this.storage.remove(key);
  }

  getTotalPageFromRange(totalPagesPerFile: number, pageRange: string) {
    let totalPage = totalPagesPerFile;
    const range = pageRange.split('-');
    if (range.length === 2) {
      totalPage = +range[1] - +range[0] + 1;
    }
    return totalPage;
  }

  createFileObj(job: Job, file: FileObj): PrintJob {
    return {
      Copies: file.copies,
      Color: file.color,
      Duplex: file.duplex,
      PaperSize: file.paperSize,
      Orientation: file.orientation,
      PageRange: file.pageRange,
      OriginalFileNameWithExt: file.originalFileNameWithExt,
      NewFileNameWithExt: file.newFileName,
      TotalPagesPerFile: file.totalPagesPerFile,
      Platform: file.platform,
      UploadedFrom: file.uploadedFrom,
      LibraryCard: job.data.libraryCard || '',
      ReleaseCode: job.data.releaseCode,
      TotalCost: job.data.totalCost,
      Username: job.data.userName,
      CustomerID: job.data.customerId,
      GuestName: job.data.guestName,
      CreatedAt: job.data.time,
      timeAgo: job.data.displayTime,
      jobStatus: job.data.jobStatus,
      locationName: job.data.locationName,
      subdomain: job.data.subdomain,
      tier: job.data.tier,
      token: job.data.token,
      refreshToken: job?.data?.refreshToken || '',
      accountId: job?.data?.accountId || 0,
      providerName: job.data.providerName || '',
      totalPage: this.getTotalPageFromRange(file.totalPagesPerFile, file.pageRange),
    };
  }

  createLocalJobObj(job: Job): GroupJob {
    const FileObj = job.data.data.map((file) => {
      return this.createFileObj(job, file);
    });
    return {
      LibraryCard: job.data.libraryCard || '',
      ReleaseCode: job.data.releaseCode,
      TotalCost: job.data.totalCost,
      Username: job.data.userName,
      CustomerID: job.data.customerId,
      GuestName: job.data.guestName,
      CreatedAt: job.data.time,
      timeAgo: job.data.displayTime,
      jobStatus: job.data.jobStatus,
      locationName: job.data.locationName,
      subdomain: job.data.subdomain,
      tier: job.data.tier,
      token: job.data.token || '',
      refreshToken: job.data.refreshToken || '',
      accountId: job.data.accountId || 0,
      key: job.key,
      providerName: job.data.providerName || '',
      files: FileObj,
    };
  }

  async getLocalJobs() {
    const jobs = await this.read();
    if (jobs && jobs.length > 0) {
      let data = jobs[0];
      if (data['data']['jobStatus'] === JobStatus.SUBMITTED) {
        data['data']['jobStatus'] = JobStatus.PROCESSING;
        this.create(jobs[0]['key'], data);
      }
      const localJobs = jobs.map((job) => this.createLocalJobObj(job));
      this.jobList = [...this.jobList, ...localJobs];
    }
    return;
  }

  manageJobData(jobs: PrintJob[]) {
    const job: GroupJob[] = [];
    let localJob: GroupJob;
    jobs.forEach((currentJob: PrintJob) => {
      currentJob.totalPage = this.getTotalPageFromRange(
        currentJob.TotalPagesPerFile,
        currentJob.PageRange
      );
      localJob = undefined;
      const jobGroup: GroupJob = job.find((job) => job.ReleaseCode === currentJob.ReleaseCode);
      if (jobGroup) {
        jobGroup.files.push(currentJob);
      } else {
        const index = this.jobList.findIndex((job) => job.ReleaseCode === currentJob.ReleaseCode);
        if (index !== -1) {
          localJob = this.jobList[index];
          this.jobList.splice(index, 1);
        }
        job.push(this.createJobObj(currentJob, localJob?.key));
      }
    });
    this.jobList = [...this.jobList, ...JSON.parse(JSON.stringify(job))];
  }

  createJobObj(job: PrintJob, key): GroupJob {
    return {
      Platform: job.Platform,
      App: job.App,
      LibraryCard: job.LibraryCard,
      LocationID: job.LocationID || null,
      ReleaseCode: job.ReleaseCode,
      Text: job.Text || null,
      TotalCost: job.TotalCost || null,
      Username: job.Username,
      AutomaticPrintDelivery: job.AutomaticPrintDelivery,
      CustomerID: job.CustomerID,
      Email: job.Email || null,
      GuestName: job.GuestName || null,
      CreatedAt: job.CreatedAt,
      IsDeleted: job.IsDeleted,
      timeAgo: job.timeAgo || '',
      token: job.token || '',
      refreshToken: job.refreshToken || '',
      accountId: job.accountId || 0,
      subdomain: job.subdomain || '',
      tier: job.tier || '',
      locationName: job.locationName || '',
      providerName: job.providerName || '',
      jobStatus: job.jobStatus || 0,
      files: [job],
      key: key,
      isLiveJob: true,
    };
  }

  removeDuplicateAccount(accountList: LoginAccount[]) {
    let acc = [];
    accountList.forEach((current) => {
      const index = acc.findIndex((item) => {
        const itemUser = item.ssoConfirmationResp.user || null;
        const currentUser = current.ssoConfirmationResp.user || null;
        return (
          itemUser.CustomerID === currentUser.CustomerID &&
          itemUser.Username === currentUser.Username
        );
      });
      index === -1 ? (acc = [...acc, current]) : (acc[index] = current);
    });
    return acc;
  }

  async loadJobs(loader?) {
    this.jobList = [];
    if (this.sharedService?.getCustomerDetails) {
      await this.getLocalJobs();
      try {
        let accountList = await this.loginAccountService.getAuthenticatedAccount();
        accountList = this.removeDuplicateAccount(accountList);
        if (accountList.length) {
          if (loader) {
            await this.loadingSvc.presentForLongTime('LOADING');
          }
          const jobsPromise = accountList.map((account) => this.getJobs(account));
          const jobs = await Promise.all(jobsPromise);
          if (jobs.length) {
            const meargeJob = jobs.reduce((acc, val) => acc.concat(val), []);
            this.manageJobData(meargeJob);
          }
        }
      } finally {
        this.sortingJob();
        this.loadingSvc.dismiss();
      }
    }
  }

  sortingJob() {
    this.jobList.sort((a: GroupJob, b: GroupJob) => {
      return (new Date(b.CreatedAt) as any) - (new Date(a.CreatedAt) as any);
    });
    this.jobList$.next(this.jobList);
  }

  async getJobs(activeAccount: LoginAccount): Promise<PrintJob[]> {
    const customerID = activeAccount.ssoConfirmationResp.user.CustomerID;
    const userName = activeAccount.ssoConfirmationResp.user.Username;
    const res: ApiResponse = await this.apiJobsService.getJobs(customerID, userName);
    if (res?.status) {
      const jobList: PrintJob[] = res.data;
      return jobList.filter((data) => {
        data.timeAgo = this.sharedService.timeAgo(data.CreatedAt);
        data.token = activeAccount.ssoConfirmationResp.token || '';
        data.refreshToken = activeAccount.ssoConfirmationResp.refreshToken || '';
        data.accountId = activeAccount.id || 0;
        data.subdomain = activeAccount.ssoConfirmationResp.user.TenantDomain || '';
        data.tier = activeAccount.ssoConfirmationResp.user.Tier || '';
        data.locationName = activeAccount.customerName || '';
        data.providerName = activeAccount.providerName || '';
        return !data.IsDeleted;
      });
    } else {
      return [];
    }
  }

  async deleteJob(job: GroupJob) {
    try {
      const variable = {
        deleteFilesInput: {
          releaseCode: job.ReleaseCode,
          fileNames: job.files.map((file) => file.NewFileNameWithExt),
          publicUploadId: null,
          customerId: job.CustomerID,
        },
      };
      const header: CustomHttpHeader = {
        Authorization: job.token,
        subdomain:
          job.subdomain || this.sharedService.getCustomerDetails.customerData.DomainName || '',
        tier: job.tier || this.sharedService.getCustomerDetails.customerData.Tier || '',
      };
      const accountId = job.accountId || 0;
      const res: DeleteFileResponse = await this.apiJobService.deleteJob(
        variable,
        header,
        accountId
      );
      if (res?.deleteFile) {
        const data = res?.deleteFile;
        if (+data.statusCode === 400) {
          this.toastService.presentErrorToast(data.message);
        } else if (+data.statusCode === 200) {
          return Promise.resolve(true);
        }
      }
    } catch (error) { }
    return Promise.resolve(false);
  }
}
